import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import { useSupporterVoicesVideoFirstTrackingProps, useUploadStepContext } from '#src/app/pages/actions/shared/hooks';

type Result = ModelHookResult<
	EmptyObject,
	{
		/** Deliberate skip by the supporter. Tracked. */
		handleSkip: () => void;
		/**
		 * The same transition without the tracking. For moving the supporter on when they did not ask
		 * to be moved - a denied camera permission, say - where recording it as a skip would put a
		 * different intent into the same metric the experiment reads.
		 */
		goToCommentStep: () => void;
	}
>;

/**
 * The single "leave the video, go write a comment" transition. Turning off
 * `shouldDisplaySupporterVoicesVideo` is what swaps `Actions` over from `CaptureVideo` to the form.
 *
 * Also resets `uploadStep` to `ready`, so a supporter who comes back to record still finds
 * `CaptureVideo` in its initial step rather than wherever they left it - the ask/recorder/preview
 * steps only ever leave for the comment from `ready` (control and R2's arms) or an R1 in-card Skip
 * (`ExitToCommentStep`), and both need this reset for the same reason.
 */
export function useSkipRecording(): Result {
	const track = useTracking();
	const [, { setShouldDisplaySupporterVoicesVideo }] = usePetitionActionsPageState();
	const { setUploadStep } = useUploadStepContext();
	const trackingProps = useSupporterVoicesVideoFirstTrackingProps();

	const goToCommentStep = useCallback(() => {
		setShouldDisplaySupporterVoicesVideo(false);
		setUploadStep('ready');
	}, [setShouldDisplaySupporterVoicesVideo, setUploadStep]);

	const handleSkip = useCallback(() => {
		void track('campaign_action_skip_recording_click', trackingProps);
		goToCommentStep();
	}, [goToCommentStep, track, trackingProps]);

	return {
		data: {},
		actions: { handleSkip, goToCommentStep },
	};
}
